import React from 'react';
import styles from './CorrectionHistory.module.css';
import type { CorrectionRecord } from './TrainingDashboard';

export interface CorrectionHistoryProps {
  corrections: CorrectionRecord[];
  maxItems?: number;
}

function severityClass(severity: string): string {
  switch (severity) {
    case 'minor':
      return styles.severityMinor;
    case 'moderate':
      return styles.severityModerate;
    case 'major':
      return styles.severityMajor;
    case 'critical':
      return styles.severityCritical;
    default:
      return styles.severityMinor;
  }
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen) + '...';
}

export const CorrectionHistory: React.FC<CorrectionHistoryProps> = ({
  corrections,
  maxItems,
}) => {
  const sorted = [...corrections].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  const display = maxItems ? sorted.slice(0, maxItems) : sorted;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Correction History</h3>
        <span className={styles.count}>{corrections.length} total</span>
      </div>

      <div className={styles.list}>
        {display.length === 0 && (
          <div className={styles.emptyState}>No corrections recorded yet</div>
        )}

        {display.map((c) => (
          <div className={styles.item} key={c.id}>
            <div className={styles.itemHeader}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className={styles.itemSkill}>{c.skill}</span>
                <span className={styles.itemType}>{c.correctionType}</span>
              </div>
              <span className={`${styles.itemSeverity} ${severityClass(c.severity)}`}>
                {c.severity}
              </span>
            </div>

            <div className={styles.changeBlock}>
              <div className={styles.changeLine}>
                <span className={`${styles.changeIndicator} ${styles.changeRemove}`}>
                  -
                </span>
                <span className={styles.changeText}>
                  {truncate(c.originalOutput, 120)}
                </span>
              </div>
              <div className={styles.changeLine}>
                <span className={`${styles.changeIndicator} ${styles.changeAdd}`}>
                  +
                </span>
                <span className={styles.changeText}>
                  {truncate(c.correctedOutput, 120)}
                </span>
              </div>
            </div>

            <div className={styles.itemFooter}>
              <span className={styles.correctedBy}>
                Corrected by{' '}
                <span className={styles.correctedByName}>{c.correctedBy}</span>
                {c.correctedByRole ? ` (${c.correctedByRole})` : ''}
              </span>
              <span className={styles.timestamp}>{formatTimestamp(c.timestamp)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
